export type Macros = {
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
};

export type IngredientCategory =
  | "protein"
  | "vegetable"
  | "fruit"
  | "grain"
  | "dairy"
  | "seasoning"
  | "fat"
  | "sweetener"
  | "liquid"
  | "other";

export const INGREDIENT_CATEGORIES: IngredientCategory[] = [
  "protein",
  "vegetable",
  "fruit",
  "grain",
  "dairy",
  "seasoning",
  "fat",
  "sweetener",
  "liquid",
  "other",
];

// Nutrition for one unit of an ingredient — "per 1 lb of chicken breast".
// Optional and rare on purpose: only ingredients that dominate a dish's macros
// AND get bought in awkward weights (meat, essentially) are worth resolving.
// Everything else stays inside the recipe's authored per-serving figure.
//
// Deliberately not something the AI recipe path fills in. Recipe macros today
// are LLM estimates, and letting the model also invent per-unit numbers would
// make the two agree by construction — which is exactly the disagreement the
// remainder guard in mealMacros.ts exists to catch.
export type MacrosPer = {
  unit: string;    // canonical-ish unit the macros are quoted per 1 of ("lb", "g")
  macros: Macros;  // nutrition in ONE of that unit
};

export type Ingredient = {
  name: string;
  base?: number | string;
  unit?: string;
  note?: string;
  category?: IngredientCategory;
  macrosPer?: MacrosPer;
};

export type IngredientGroup = {
  label?: string;
  ingredients: Ingredient[];
};

export type Step = {
  title?: string;
  text?: string;
  bullets?: string[];
  note?: string;
  timer?: number;
};

// Work that can be done a day or two before you cook without the dish being
// any worse for it — the spice blend mixed, the onions chopped, the sauce
// whisked. Deliberately NOT a Step: these are optional, order-free, and dated
// relative to cooking day rather than to each other, so folding them into
// `steps` would put "do this on Sunday" between two things you do at the stove.
export type PrepAheadItem = {
  title: string;      // "Mix the spice rub"
  text?: string;      // how to do it
  when?: string;      // how far ahead it keeps: "Up to 2 weeks ahead"
  storage?: string;   // how to hold it until then: "Airtight jar, pantry"
};

export type Note = {
  title?: string;
  body?: string;
};

export type Source = {
  name: string;
  type: "website" | "cookbook" | "magazine" | "personal" | "other";
  url?: string;
  page?: string;
};

export type AIProvenanceReference = {
  title: string;
  url: string;
};

export type AIProvenance = {
  model: string;
  prompt: string;
  reasoning?: string;
  modifications?: string[];
  references?: AIProvenanceReference[];
};

export type Recipe = {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  category?: string;
  servings?: number;
  imageUrl?: string;
  tags?: string[];
  macros?: Macros;
  ingredientGroups?: IngredientGroup[];
  prepAhead?: PrepAheadItem[];
  steps?: Step[];
  notes?: Note[];
  sources?: Source[];
  aiProvenance?: AIProvenance;
  currentRevisionNumber?: number;
  // Shelf state, not recipe content: an archived recipe keeps every revision
  // and stays reachable by id, it just leaves the cookbook's shelves. Never
  // written into revision snapshots — see updateRecipe/restoreRevision, which
  // carry it forward on the live doc instead.
  archived?: boolean;
  archivedAt?: string;
  // Lineage, not recipe content: where this copy came from when it wasn't
  // written from scratch. `copiedFrom` is another recipe of your own (see
  // copyRecipe); `forkedFrom` is someone else's, taken off the Kitchen Table.
  // Both follow the archive rule above — kept out of revision snapshots and
  // carried forward on the live doc, so an edit can't quietly orphan a copy.
  copiedFrom?: { recipeId: string; title: string; revisionNumber?: number };
  copiedAt?: string;
  forkedFrom?: { ownerId: string; shareId: string; originalId: string };
  forkedAt?: string;
};

// ── User preferences ────────────────────────────────────────────────
// Stored as a `preferences` map field on the users/{uid} document. Daily
// targets are the cook's nutrition goals (used by On Deck progress and AI
// planning); appliances are the equipment they own, which AI recipe generation
// respects so it never assumes a tool they don't have.
export type MacroTargets = {
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
};

export type UserPreferences = {
  dailyTargets?: MacroTargets;
  appliances?: string[]; // appliance ids from src/lib/appliances.ts
  // Ask, at each meal's hour, whether a planned meal actually happened.
  // Opt-in: notifications nobody asked for are the fastest way to make an app
  // something you mute.
  mealReminders?: boolean;
};

// ── On Deck (meal planning) ─────────────────────────────────────────
// A day's plan is organized into these meal slots, in this order.
export type MealSlot = "breakfast" | "lunch" | "dinner" | "snack";

export const MEAL_SLOTS: MealSlot[] = ["breakfast", "lunch", "dinner", "snack"];

// A single recipe placed into a day's slot. title is denormalized so the board
// still reads sensibly if the underlying recipe is later renamed or deleted;
// macros are always resolved live from the cookbook recipe by recipeId.
export type PlannedMeal = {
  id: string;          // stable key for the array (crypto.randomUUID)
  recipeId: string;
  title: string;
  slot: MealSlot;
  servings: number;    // portions planned for this meal; defaults to 1
  // Ticked once the meal was actually eaten, which is a different claim from
  // having planned it. Only eaten meals are sent to Apple Health — a plan is an
  // intention, and a food diary should hold what happened.
  eaten?: boolean;
  // What you ACTUALLY put in this one meal, for ingredients where the recipe's
  // number and the shop's number disagree — meat comes in 2 lb packs, not the
  // 1.5 lb the recipe asked for. Keyed by normalized ingredient name (see
  // normalizeName in shoppingList.ts), valued in the ingredient's OWN unit.
  //
  // Stored ALREADY DIVIDED — the amount for this meal, not the batch. Buying
  // 2 lb for three planned dinners writes 0.667 onto each, so a meal stays
  // self-contained and nothing has to look up its siblings to know what it's
  // worth. Same reason SendToWeek writes independent meals per date.
  //
  // Only shifts macros for ingredients carrying macrosPer; otherwise it just
  // corrects what the shopping list asks you to buy.
  ingredientAmounts?: Record<string, number>;
};

// One day of the plan. Stored at users/{uid}/onDeck/{date} with date as the
// doc id, so a week is just seven point reads. Empty days are not persisted.
export type PlanDay = {
  date: string;            // YYYY-MM-DD (local), also the Firestore doc id
  meals: PlannedMeal[];
  targetCalories?: number; // optional daily calorie goal
};

export type Revision = {
  _docId: string;
  number: number;
  savedAt?: string;
  // What changed in this version — the headline in the revision list.
  note?: string;
  releaseName?: string;
  // The cook's verdict on the version, written after actually making it:
  // stars 1–5 and a personal note ("too salty — halve the soy"). Set from the
  // Revisions page or the MCP rate_revision tool. Private: never copied into
  // a share's exposedRevisions.
  rating?: number;
  outcome?: string;
  snapshot?: Recipe;
  fields?: Array<{ path: string; from: unknown; to: unknown; kind?: string }>;
};

export type SharedRecipe = {
  shareId: string;
  ownerId: string;
  ownerName?: string;
  originalRecipeId: string;
  // "public" = listed on the Kitchen Table. "private" = link/pass only:
  // readable by anyone who has the share id (the link), but not listed.
  // Undefined on legacy docs is treated as public.
  visibility?: "public" | "private";
  title: string;
  subtitle?: string;
  description?: string;
  category?: string;
  tags?: string[];
  imageUrl?: string;
  macros?: Macros;
  publishedAt?: { seconds: number } | string;
  updatedAt?: { seconds: number } | string;
  forkCount?: number;
  commentCount?: number;
  bookmarkCount?: number;
  currentRevisionNumber?: number;
  latestSnapshot?: Recipe;
  exposedRevisions?: Array<{
    number: number;
    note?: string;
    releaseName?: string;
    savedAt?: string;
    snapshot?: Recipe;
  }>;
};

// How a passed recipe tracks its source:
//  - "live"     → always shows the sender's latest version (the dynamic head)
//  - "snapshot" → pinned to a specific, frozen revision
export type PassMode = "live" | "snapshot";

// A directed, friend-to-friend delivery of a recipe. Points at a sharedRecipes
// doc (the readable artifact) plus, for snapshot mode, the pinned revision.
// Addressed by recipient uid — only allowed between accepted friends.
export type RecipeSend = {
  sendId: string;
  fromUid: string;
  fromName?: string;
  toUid: string;
  toName?: string;
  shareId: string;
  mode: PassMode;
  revisionNumber?: number;   // set only for mode === "snapshot"
  title: string;
  subtitle?: string;
  imageUrl?: string;
  message?: string;
  sentAt: string;
  status: "pending" | "saved" | "dismissed";
};

// A searchable, minimal public identity. Display name only — never email.
// Written on sign-in; read by any signed-in user to find people to befriend.
export type PublicProfile = {
  uid: string;
  displayName: string;
  displayNameLower?: string;  // for prefix search
  photoURL?: string;
};

// A friendship between two users. Doc id is the sorted uid pair so there is
// exactly one doc per relationship regardless of who initiated it.
export type Connection = {
  id: string;
  users: string[];            // the two uids, sorted
  requestedBy: string;        // who sent the request
  status: "pending" | "accepted";
  names?: Record<string, string>;  // uid -> displayName, denormalized
  createdAt?: string;
  respondedAt?: string;
};

// A connection viewed from the current user's perspective.
export type Friend = {
  uid: string;
  displayName: string;
  status: "pending" | "accepted";
  direction: "incoming" | "outgoing" | "friends";
  connectionId: string;
};
